import React, { useState, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import Spinner from "../spinner/Spinner";
import {
  useVendorPaymentById,
  useCreateVendorPayment,
  useUpdateVendorPayment,
  useSupplyVendorsDropdown,
  useBankAccountsDropdown,
  useAdminPurchasesDropdown,
} from "../../hooks/useInventory";

const emptyForm = {
  supply_vendor_id: "",
  company_bank_account_id: "",
  payment_date: "",
  amount: "",
  order_number: "",
  narration: "",
  admin_purchase_id: "",
};

const VendorPaymentForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get("id");
  const presetPurchaseId = searchParams.get("purchase_id");
  const isEdit = !!paymentId;

  const { data: existing, isLoading: loadingExisting } = useVendorPaymentById(paymentId);
  const { data: vendorsData } = useSupplyVendorsDropdown();
  const { data: banksData } = useBankAccountsDropdown();
  const { data: purchasesData } = useAdminPurchasesDropdown();
  const createMutation = useCreateVendorPayment();
  const updateMutation = useUpdateVendorPayment();

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const vendors = vendorsData?.data || [];
  const banks = banksData?.data || [];
  const purchases = purchasesData?.data || [];

  // Set preset purchase ID on initial load (only when not editing)
  useEffect(() => {
    if (!isEdit && presetPurchaseId) {
      setForm((prev) => ({ ...prev, admin_purchase_id: presetPurchaseId }));
    }
  }, [presetPurchaseId, isEdit]);

  useEffect(() => {
    if (existing?.data) {
      const p = existing.data.payment || existing.data;
      const toStr = (v) => (v === null || v === undefined ? "" : String(v));
      // Format datetime for datetime-local input (Y-m-d\TH:i)
      const formatDatetimeLocal = (date) => {
        if (!date) return "";
        const d = new Date(date);
        return d.toISOString().slice(0, 16);
      };
      setForm({
        supply_vendor_id: toStr(p.supply_vendor_id ?? p.vendor?.id),
        company_bank_account_id: toStr(p.company_bank_account_id ?? p.bank_account?.id),
        payment_date: formatDatetimeLocal(p.payment_date),
        amount: toStr(p.amount).replace(/,/g, ""),
        order_number: p.order_number || "",
        narration: p.narration || "",
        admin_purchase_id: toStr(p.admin_purchase_id ?? p.admin_purchase?.id),
      });
    }
  }, [existing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.supply_vendor_id) errs.supply_vendor_id = "Vendor is required";
    if (!form.company_bank_account_id) errs.company_bank_account_id = "Bank account is required";
    if (!form.payment_date) errs.payment_date = "Payment date is required";
    if (!form.amount || parseFloat(form.amount) <= 0) errs.amount = "Valid amount is required";
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      ...form,
      supply_vendor_id: parseInt(form.supply_vendor_id),
      company_bank_account_id: parseInt(form.company_bank_account_id),
      amount: parseFloat(form.amount),
      admin_purchase_id: form.admin_purchase_id ? parseInt(form.admin_purchase_id) : undefined,
    };

    if (isEdit) payload.payment_id = parseInt(paymentId);

    const mutation = isEdit ? updateMutation : createMutation;
    mutation.mutate(payload, { onSuccess: () => navigate("/head-office/inventory/vendor-payments") });
  };

  const isPending = createMutation.isLoading || updateMutation.isLoading;
  if (isEdit && loadingExisting) return <MainLayouts><Spinner loading /></MainLayouts>;

  const inputCls = (name) =>
    `w-full border ${errors[name] ? "border-red-500" : "border-gray-300"} rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1`;

  return (
    <MainLayouts>
      <div className="p-4">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/head-office/inventory/vendor-payments")} className="text-orange1 hover:text-orange-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold">{isEdit ? "Edit Payment" : "Record Vendor Payment"}</h1>
        </div>

        <div className="bg-white rounded-lg shadow p-4 w-full">
          <form onSubmit={handleSubmit}>
            <fieldset disabled={isPending} className="disabled:opacity-60">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor <span className="text-red-500">*</span></label>
                  <select name="supply_vendor_id" value={form.supply_vendor_id} onChange={handleChange} className={inputCls("supply_vendor_id")}>
                    <option value="">Select vendor...</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  {errors.supply_vendor_id && <p className="text-red-500 text-xs mt-1">{errors.supply_vendor_id}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account <span className="text-red-500">*</span></label>
                  <select name="company_bank_account_id" value={form.company_bank_account_id} onChange={handleChange} className={inputCls("company_bank_account_id")}>
                    <option value="">Select bank account...</option>
                    {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} - {b.account_name}</option>)}
                  </select>
                  {errors.company_bank_account_id && <p className="text-red-500 text-xs mt-1">{errors.company_bank_account_id}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date <span className="text-red-500">*</span></label>
                  <input type="datetime-local" name="payment_date" value={form.payment_date} onChange={handleChange} className={inputCls("payment_date")} />
                  {errors.payment_date && <p className="text-red-500 text-xs mt-1">{errors.payment_date}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₦) <span className="text-red-500">*</span></label>
                  <input type="number" name="amount" value={form.amount} onChange={handleChange} min="0.01" step="0.01" className={inputCls("amount")} placeholder="0.00" />
                  {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order / Reference Number</label>
                  <input name="order_number" value={form.order_number} onChange={handleChange} className={inputCls("order_number")} placeholder="Optional" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Linked Purchase (optional)</label>
                  <select name="admin_purchase_id" value={form.admin_purchase_id} onChange={handleChange} className={inputCls("admin_purchase_id")}>
                    <option value="">Select purchase </option>
                    {purchases.map((p) => <option key={p.id} value={p.id}>{p.purchase_number || `#${p.id}`}</option>)}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Narration</label>
                  <textarea name="narration" value={form.narration} onChange={handleChange} rows={3} className={inputCls("narration")} placeholder="Optional notes about this payment..." />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => navigate("/head-office/inventory/vendor-payments")} disabled={isPending} className="px-5 py-2 border border-gray-300 rounded hover:bg-gray-50 transition text-sm disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={isPending} className="px-5 py-2 bg-orange1 text-white rounded hover:bg-orange-600 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isPending ? "Saving..." : isEdit ? "Update Payment" : "Record Payment"}
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </MainLayouts>
  );
};

export default VendorPaymentForm;
